import createClient, { type Middleware } from "openapi-fetch";

import { getCookie } from "@/lib/cookies";

import { ApiError, toApiError } from "./errors";
import type { paths } from "./generated/schema";

const CSRF_COOKIE = "kshop_csrftoken";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/** Session-expiry listeners (the app re-reads the session and routes to sign-in). */
const unauthenticatedListeners = new Set<() => void>();
export function onUnauthenticated(listener: () => void): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}

export async function ensureCsrfCookie(): Promise<void> {
  if (!getCookie(CSRF_COOKIE)) await fetch("/api/auth/csrf/", { credentials: "same-origin" });
}

const csrfMiddleware: Middleware = {
  async onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method)) {
      await ensureCsrfCookie();
      const token = getCookie(CSRF_COOKIE);
      if (token) request.headers.set("X-CSRFToken", token);
    }
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401 || response.status === 403) {
      const body = await response.clone().json().catch(() => null);
      const code = body?.error?.code;
      if (code === "not_authenticated" || code === "authentication_failed") {
        unauthenticatedListeners.forEach((fn) => fn());
      }
    }
    return response;
  },
};

// Same-origin API (Vite proxy in development, reverse proxy in production). An absolute base URL also
// works in non-browser runtimes (tests), where Request rejects relative URLs.
export const api = createClient<paths>({ baseUrl: window.location.origin, credentials: "same-origin" });
api.use(csrfMiddleware);

type Result<T> = { data?: T; error?: unknown; response: Response };

/** Return data or throw a normalized ApiError. */
export async function unwrap<T>(promise: Promise<Result<T>>): Promise<T> {
  let result: Result<T>;
  try {
    result = await promise;
  } catch {
    throw toApiError(0, null);
  }
  if (!result.response.ok) throw toApiError(result.response.status, result.error);
  return result.data as T;
}

/** Multipart upload (openapi-fetch focuses on JSON), with any other form `fields`; same CSRF and error handling. */
export async function uploadFile<T>(url: string, file: File | null, method: "POST" | "DELETE" = "POST", fields: Record<string, string> = {}): Promise<T> {
  await ensureCsrfCookie();
  const body = new FormData();
  if (file) body.append("file", file);
  for (const [key, value] of Object.entries(fields)) body.append(key, value);
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      body: method === "POST" ? body : undefined,
      credentials: "same-origin",
      headers: { "X-CSRFToken": getCookie(CSRF_COOKIE) ?? "" },
    });
  } catch {
    throw toApiError(0, null);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) throw toApiError(response.status, data);
  return data as T;
}

/** POST JSON to a URL built at run time (list group actions, whose paths differ per list); same CSRF
 * and error handling as `api`. */
export async function postJson<T>(url: string, body: unknown): Promise<T> {
  return sendJson<T>("POST", url, body);
}

/** Send JSON (or nothing, for DELETE) to a URL built at run time (records' photos, whose paths differ per owner). */
export async function sendJson<T>(method: "POST" | "PATCH" | "DELETE", url: string, body?: unknown): Promise<T> {
  await ensureCsrfCookie();
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRFToken": getCookie(CSRF_COOKIE) ?? "" },
    });
  } catch {
    throw toApiError(0, null);
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) throw toApiError(response.status, data);
  return data as T;
}

/** POST JSON and save the file the server answers with (CSV exports), named by its Content-Disposition. */
export async function downloadPost(url: string, body: unknown, fallbackName: string): Promise<void> {
  await ensureCsrfCookie();
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(body),
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRFToken": getCookie(CSRF_COOKIE) ?? "" },
    });
  } catch {
    throw toApiError(0, null);
  }
  if (!response.ok) throw toApiError(response.status, await response.json().catch(() => null));
  const name = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? fallbackName;
  const href = URL.createObjectURL(await response.blob());
  const link = Object.assign(document.createElement("a"), { href, download: name });
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

export { ApiError };
